/**
 * DynamicSubTable Component
 * 
 * A utility component that dynamically generates PrimeReact Column components from configuration objects.
 * This component is designed to work within PrimeReact DataTable components to create flexible,
 * configurable table columns without hardcoding column definitions.
 * 
 * Purpose: 
 * - Provides dynamic column generation based on configuration arrays
 * - Enables reusable table structures across different data types 
 * 
 * Features:
 * - Dynamic Column generation from configuration objects
 * - Configurable column styling (width, height, padding, etc.) 
 * 
 * @param {Object} props - Component props
 * @param {Array} props.subColDatas - Array of column configuration objects
 * @param {string} props.defaultHeight - Default height for columns when not specified (default: '35px')
 * 
 * @returns {Array<JSX.Element>|null} Array of PrimeReact Column components or null if invalid input
 *  
 */

import React from 'react';
import { Column } from 'primereact/column';

const DynamicSubTable = ({ subColDatas, defaultHeight = '35px' }) => {
    if (!subColDatas || !Array.isArray(subColDatas)) {
        return null;
    }

    return subColDatas.map((col) => (
        <Column
            key={col.field}
            field={col.field}
            header={col.header}
            sortable={col.sortable}
            style={{
                width: col.width,
                height: col.height || defaultHeight,
                paddingLeft: col.paddingLeft,
                padding: col.padding,
                display: col.display,
                ...col.style
            }}
            {...col}
        />
    ));
};

export default DynamicSubTable;